import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Bitcoin, Eye, EyeOff, TrendingUp, TrendingDown, Info, Search } from 'lucide-react';
import { useDatabase, DatabaseUserAsset } from '../hooks/useDatabase';
import { useMarketData } from '../contexts/MarketDataContext';
import { useBybitData } from '../contexts/BybitDataContext';

interface CryptoHoldingsProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  userAssets?: DatabaseUserAsset[];
  showBalance?: boolean;
  marketData?: unknown[];
}

interface CryptoAsset {
  symbol: string;
  name: string;
  icon: React.ReactNode;
  balance: number;
  usdValue: number;
  change24h?: number;
  color: string;
}

const MASKED_VALUE = '******';

const CryptoHoldings: React.FC<CryptoHoldingsProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  userAssets = [],
  showBalance
}) => {
  const { t } = useTranslation();
  const { assets } = useDatabase();
  const { marketData, snapshotData, getSnapshotPriceBySymbol } = useMarketData();
  const { getPriceBySymbol: getBybitPrice } = useBybitData();
  const [showBalances, setShowBalances] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cryptoAssets, setCryptoAssets] = useState<CryptoAsset[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [totalChange, setTotalChange] = useState(0);

  const getAccentChipClass = (color: string) => {
    switch (color) {
      case 'green':
        return 'bg-emerald-500/15 text-emerald-300';
      case 'orange':
        return 'bg-amber-500/15 text-amber-300';
      case 'blue':
        return 'bg-blue-500/15 text-blue-300';
      case 'purple':
        return 'bg-purple-500/15 text-purple-300';
      case 'indigo':
        return 'bg-indigo-500/15 text-indigo-300';
      case 'pink':
        return 'bg-pink-500/15 text-pink-300';
      case 'red':
        return 'bg-rose-500/15 text-rose-300';
      case 'yellow':
        return 'bg-yellow-500/15 text-yellow-300';
      case 'emerald':
        return 'bg-emerald-500/15 text-emerald-300';
      case 'teal':
        return 'bg-teal-500/15 text-teal-300';
      case 'cyan':
        return 'bg-cyan-500/15 text-cyan-300';
      default:
        return 'bg-purple-500/15 text-purple-300';
    }
  };

  const getPriceForSymbol = useCallback((symbol: string): number => {
    if (symbol === 'USDT' || symbol === 'USDC') {
      return 1;
    }

    const tradingPair = `${symbol}USDT`;

    const bybitPrice = getBybitPrice(tradingPair);
    if (bybitPrice > 0) {
      return bybitPrice;
    }

    const snapshotPrice = getSnapshotPriceBySymbol(tradingPair);
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    const marketDataItem = marketData.find(item => item.symbol === tradingPair);
    if (marketDataItem && marketDataItem.price > 0) {
      return marketDataItem.price;
    }

    const snapshotItem = snapshotData.find(item => item.symbol === tradingPair);
    if (snapshotItem && snapshotItem.price > 0) {
      return snapshotItem.price;
    }

    if (symbol === 'BTC' && currentBtcPrice > 0) {
      return currentBtcPrice;
    }

    console.warn(`CryptoHoldings: No price found for ${symbol}`);
    return 0;
  }, [getBybitPrice, getSnapshotPriceBySymbol, marketData, snapshotData, currentBtcPrice]);

  useEffect(() => {
    const assetList: CryptoAsset[] = [];
    let portfolioValue = 0;
    let weightedChange = 0;

    if (usdtBalance > 0) {
      assetList.push({
        symbol: 'USDT',
        name: 'Tether',
        icon: <DollarSign size={20} className="text-emerald-300" />,
        balance: usdtBalance,
        usdValue: usdtBalance,
        change24h: 0,
        color: 'green'
      });
      portfolioValue += usdtBalance;
    }

    if (btcBalance > 0) {
      const btcPrice = getPriceForSymbol('BTC');
      const btcValue = btcBalance * btcPrice;
      const btcMarketData = marketData.find(data => data.symbol === 'BTCUSDT');
      const btcChangePercentage = btcMarketData?.change_24h || 0;
      const btcValueChange = btcValue * (btcChangePercentage / 100);

      assetList.push({
        symbol: 'BTC',
        name: 'Bitcoin',
        icon: <Bitcoin size={20} className="text-amber-300" />,
        balance: btcBalance,
        usdValue: btcValue,
        change24h: btcChangePercentage,
        color: 'orange'
      });

      portfolioValue += btcValue;
      weightedChange += btcValueChange;
    }

    const allAssets = [...userAssets, ...(assets || [])];
    const assetMap = new Map();

    allAssets.forEach(asset => {
      if (asset.asset_symbol === 'BTC' || asset.asset_symbol === 'USDT') return;

      if (assetMap.has(asset.asset_symbol)) {
        const existing = assetMap.get(asset.asset_symbol);
        if (asset.balance > existing.balance) {
          assetMap.set(asset.asset_symbol, asset);
        }
      } else {
        assetMap.set(asset.asset_symbol, asset);
      }
    });

    Array.from(assetMap.values()).forEach(asset => {
      if (asset.balance > 0) {
        const price = getPriceForSymbol(asset.asset_symbol);
        const assetMarketData = marketData.find(data => data.symbol === `${asset.asset_symbol}USDT`);
        const changePercentage = assetMarketData?.change_24h || 0;

        if (price > 0) {
          const usdValue = asset.balance * price;
          const valueChange = usdValue * (changePercentage / 100);
          const colors = ['blue', 'purple', 'indigo', 'pink', 'red', 'yellow', 'emerald', 'teal', 'cyan'];
          const colorIndex = asset.asset_symbol.charCodeAt(0) % colors.length;
          const accentColor = colors[colorIndex];

          assetList.push({
            symbol: asset.asset_symbol,
            name: asset.asset_symbol,
            icon: (
              <div className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${getAccentChipClass(accentColor)}`}>
                {asset.asset_symbol.substring(0, 2)}
              </div>
            ),
            balance: asset.balance,
            usdValue,
            change24h: changePercentage,
            color: accentColor
          });

          portfolioValue += usdValue;
          weightedChange += valueChange;
        }
      }
    });

    const totalChangePercentage = portfolioValue > 0 ? (weightedChange / portfolioValue) * 100 : 0;

    setCryptoAssets(assetList);
    setTotalValue(portfolioValue);
    setTotalChange(totalChangePercentage);
  }, [usdtBalance, btcBalance, marketData, userAssets, assets, getPriceForSymbol]);

  useEffect(() => {
    if (typeof showBalance === 'boolean') {
      setShowBalances(showBalance);
    }
  }, [showBalance]);

  const filteredAssets = cryptoAssets.filter(asset => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return asset.symbol.toLowerCase().includes(term) || asset.name.toLowerCase().includes(term);
  });

  const primaryCardClass =
    'rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/24 via-indigo-500/18 to-purple-500/16 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
  const secondaryCardClass =
    'rounded-2xl border border-purple-500/35 bg-gradient-to-br from-blue-500/22 via-indigo-500/16 to-purple-500/18 shadow-2xl shadow-purple-500/15 backdrop-blur-sm';
  const innerCardClass =
    'rounded-xl border border-purple-500/25 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12';
  const innerCardHoverClass =
    'transition-all duration-300 hover:border-purple-400/45 hover:from-indigo-500/16 hover:via-purple-500/16 hover:to-fuchsia-500/16';
  const searchInputClass =
    'purple-form-field w-full rounded-xl border border-purple-500/30 bg-gradient-to-r from-indigo-950/72 via-purple-950/72 to-fuchsia-950/70 py-2 pl-10 pr-4 text-sm text-white placeholder:text-slate-400 transition-all focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-400/55';
  const countPillClass =
    'rounded-full border border-purple-500/20 bg-purple-950/40 px-3 py-1 text-xs text-purple-200';
  const infoCardClass =
    'rounded-xl border border-purple-400/25 bg-gradient-to-r from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12 p-4';

  return (
    <div className="space-y-6">
      <div className={`p-6 ${primaryCardClass}`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-purple-500/25">
              <DollarSign size={20} className="text-white" />
            </span>
            {t('wallet.totalPortfolioValue')}
          </h3>
          <button
            onClick={() => setShowBalances(!showBalances)}
            className="rounded-lg border border-purple-500/20 bg-purple-950/40 p-2 text-slate-300 transition-colors hover:border-purple-400/40 hover:text-white"
          >
            {showBalances ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="mb-2 text-3xl font-bold text-white">
          {showBalances
            ? `$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : MASKED_VALUE}
        </div>

        <div className={`flex items-center gap-1 text-sm ${totalChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {totalChange >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {totalChange >= 0 ? '+' : ''}
          {totalChange.toFixed(2)}% ({t('wallet.24h')})
        </div>
      </div>

      <div className={`p-6 ${secondaryCardClass}`}>
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{t('wallet.cryptoHoldings')}</h3>
          <div className={countPillClass}>
            {cryptoAssets.length} {t('wallet.assets')}
          </div>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={t('wallet.searchAssets')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={searchInputClass}
          />
        </div>

        <div className="space-y-4">
          {filteredAssets.map((asset) => (
            <div key={asset.symbol} className={`flex items-center justify-between p-4 ${innerCardClass} ${innerCardHoverClass}`}>
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${getAccentChipClass(asset.color)}`}>
                  {asset.icon}
                </div>
                <div>
                  <div className="font-medium text-white">{asset.name}</div>
                  <div className="text-sm text-slate-400">{asset.symbol}</div>
                </div>
              </div>

              <div className="text-right">
                <div className="font-bold text-white">
                  {showBalances ? asset.balance.toFixed(asset.symbol === 'USDT' ? 2 : 6) : MASKED_VALUE}
                </div>
                <div className="flex items-center justify-end gap-1 text-sm">
                  <span className="text-slate-400">
                    $
                    {showBalances
                      ? asset.usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : MASKED_VALUE}
                  </span>
                  {asset.change24h !== undefined && asset.change24h !== 0 && (
                    <span className={`flex items-center ${asset.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {asset.change24h >= 0 ? (
                        <TrendingUp size={12} className="mr-1" />
                      ) : (
                        <TrendingDown size={12} className="mr-1" />
                      )}
                      {asset.change24h >= 0 ? '+' : ''}
                      {asset.change24h.toFixed(2)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredAssets.length === 0 && (
            <div className="py-8 text-center text-slate-400">
              {t('wallet.noAssetsFound', { searchTerm })}
            </div>
          )}
        </div>

        <div className={`mt-6 flex items-start gap-3 ${infoCardClass}`}>
          <Info size={18} className="mt-0.5 shrink-0 text-purple-200" />
          <div>
            <p className="text-sm font-medium text-purple-100">{t('wallet.aboutCryptoHoldings')}</p>
            <p className="mt-1 text-xs text-slate-300">{t('wallet.aboutCryptoHoldingsDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoHoldings;
